// C.1 — Подкрепление, фракционная аура Глобала.
//
// Правило (диздок v2.9):
//   - На каждой линии считается количество СОЮЗНЫХ юнитов с faction='global'.
//   - 1 юнит = +0; 2 или 3 юнита = +1; 4 юнита = +2.
//   - На 1-й линии (front, line=0) бонус идёт в атаку.
//   - На 2-й линии (back, line=1) бонус идёт в hp (max и current синхронно).
//   - Считаются только Глобал-юниты (Neutral не учитывается).
//   - Аура работает только если у игрока Герой Глобала (faction='global').
//   - Под Молчанием юнит не теряет бонус (это фракционная аура, не его
//     keyword); Молчание не блокирует Подкрепление.
//
// Реализация:
//   - Применяется как PermanentBuff с sourceId = REINFORCEMENT_SOURCE.
//   - При каждом изменении расклада (onUnitSummoned/Died/Moved) — функция
//     recomputeReinforcement очищает все Подкрепление-баффы и применяет
//     заново по актуальному количеству.
//
// Подключение: вызывать recomputeReinforcement(state) рядом с
// recomputeLineTriggers в hooks Деуса (которые сейчас — общая шина
// onUnitSummoned/Died/Moved) и в move-unit.js.

import { applyPermanentBuff, removePermanentBuffsBySource } from '../buffs.js';

export const REINFORCEMENT_SOURCE = 'reinforcement';

function reinforcementMatcher(sourceId) {
  return typeof sourceId === 'string' && sourceId.startsWith(REINFORCEMENT_SOURCE);
}

// Вычисляем бонус подкрепления по количеству Глобал-юнитов на линии.
//   count=0..1 → 0
//   count=2..3 → 1
//   count=4    → 2
// Под аурой Полевой штаб «Кулак» (state.reinforcementPerOneActive[ownerIdx]) —
// формула меняется на «+1 за каждого союзника начиная со второго»:
//   count=0..1 → 0
//   count=2    → 1
//   count=3    → 2
//   count=4    → 3
function reinforcementBonus(count, perOne = false) {
  if (perOne) return Math.max(0, count - 1);
  return Math.floor(count / 2);
}

export function recomputeReinforcement(state) {
  if (!state || !Array.isArray(state.players)) return;

  for (let pIdx = 0; pIdx < state.players.length; pIdx++) {
    const p = state.players[pIdx];
    if (!p || !p.field) continue;

    // Подкрепление работает только если у игрока Герой Глобала.
    const heroFaction = p.hero?.faction;
    const heroIsGlobal = heroFaction === 'global';
    const perOne = !!(state.reinforcementPerOneActive?.[pIdx]);

    // БУГ.1 (Бастион HP): раньше код безусловно снимал ВСЕ reinforcement-буфы
    // и заново применял их при каждом пересчёте. Это «лечило» юнита, потому
    // что applyPermanentBuff(+hp) добавляет к current.hp, а
    // removePermanentBuffsBySource снижает current.hp ТОЛЬКО если current>base.
    // Цикл remove+re-apply возвращал юниту полные HP после полученного урона.
    //
    // Фикс — идемпотентность: для каждого юнита сравниваем фактический
    // примененный бафф с тем, что должен быть. Если совпадает — пропускаем
    // remove+apply. Если меняется (другой бонус / другая линия / юнит больше
    // не Глобал / лига вне аура-окна) — обновляем.
    for (let line = 0; line < 2; line++) {
      const row = p.field[line];
      if (!row) continue;
      let count = 0;
      for (const u of row) {
        if (u && u.faction === 'global') count++;
      }
      const bonus = heroIsGlobal ? reinforcementBonus(count, perOne) : 0;
      const newAtk = (line === 0) ? bonus : 0;
      const newHp = (line === 1) ? bonus : 0;
      const sourceId = `${REINFORCEMENT_SOURCE}:line${line}:${count}`;
      const sourceLabel = `Подкрепление (л.${line + 1}, ${count} юнитов)`;

      for (const u of row) {
        if (!u) continue;
        const oldBuff = (u.permanentBuffs || []).find((b) => reinforcementMatcher(b.id));
        const oldAtk = oldBuff?.atk || 0;
        const oldHp = oldBuff?.hp || 0;

        // Не-Глобал юнит на линии (mind-control / угон) — снять, если был.
        if (u.faction !== 'global') {
          if (oldBuff) removePermanentBuffsBySource(state, u, reinforcementMatcher);
          continue;
        }

        // Идемпотентность: бонус не изменился — пропускаем.
        if (oldAtk === newAtk && oldHp === newHp) continue;

        // Бонус изменился — снять старый (если был) и применить новый.
        if (oldBuff) removePermanentBuffsBySource(state, u, reinforcementMatcher);
        if (bonus > 0) {
          const delta = (line === 0)
            ? { atk: bonus, armor: 0, hp: 0 }
            : { atk: 0, armor: 0, hp: bonus };
          applyPermanentBuff(state, u, delta, sourceId, sourceLabel);
        }
      }
    }
  }
}

// Удобный хук для вызова из onUnitDied / onUnitLeftField / onUnitMoved.
// Простая обёртка с защитой от пустого state.
export function onReinforcementRelevantChange(state) {
  if (!state) return;
  try { recomputeReinforcement(state); } catch (e) {
    console.warn('[Корпы] recomputeReinforcement failed:', e?.message || e);
  }
}
